/**
 * @module domain/query
 * @description Pure search, filter, sort and facet-count logic (brief §4.3).
 *
 * No React here: the browse UI calls these, and the same functions will run
 * against WordPress data in production.
 */
import { URGENCY_WEIGHT } from "../config/taxonomy";
import { daysLeft } from "../lib/format";
import type { Cause, CauseQuery, CategoryId, ParishId, SortId, UrgencyId } from "./types";

/** The unfiltered starting state. */
export const EMPTY_QUERY: CauseQuery = {
    text: "",
    categories: [],
    parishes: [],
    urgencies: [],
    tags: [],
    sort: "popular",
};

/** @returns true when any filter (not sort) is active; drives "Clear filters". */
export function hasActiveFilters(query: CauseQuery): boolean {
    return query.text.trim() !== "" || query.categories.length > 0 || query.parishes.length > 0
        || query.urgencies.length > 0 || query.tags.length > 0;
}

/** Lowercased haystack for keyword search: title, partner, summary, tags. */
function searchText(cause: Cause): string {
    return [cause.title, cause.partner, cause.summary, ...cause.tags].join(" ").toLowerCase();
}

/**
 * Keyword match: every whitespace-separated term must appear (AND), so
 * "school kingston" narrows rather than widens.
 */
export function matchesText(cause: Cause, text: string): boolean {
    const terms: string[] = text.toLowerCase().split(/\s+/).filter((t: string) => t !== "");
    const haystack: string = searchText(cause);
    return terms.every((term: string) => haystack.includes(term));
}

/** Facet match: an empty selection means "no constraint" (OR within a facet). */
function inSelection<T>(value: T, selection: readonly T[]): boolean {
    return selection.length === 0 || selection.includes(value);
}

/** Comparator per sort option (brief §4.3 order). */
const COMPARATORS: Readonly<Record<SortId, (a: Cause, b: Cause) => number>> = {
    popular: (a, b) => a.popularityRank - b.popularityRank,
    urgent: (a, b) => URGENCY_WEIGHT[a.urgency] - URGENCY_WEIGHT[b.urgency]
        || daysLeft(a.funding.endsAt) - daysLeft(b.funding.endsAt),
    newest: (a, b) => b.createdAt.localeCompare(a.createdAt),
    updated: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
};

/** @returns causes matching every active filter, sorted. Never mutates input. */
export function applyQuery(causes: readonly Cause[], query: CauseQuery): Cause[] {
    return causes
        .filter((c: Cause) =>
            matchesText(c, query.text)
            && inSelection<CategoryId>(c.category, query.categories)
            && inSelection<ParishId>(c.parish, query.parishes)
            && inSelection<UrgencyId>(c.urgency, query.urgencies)
            && (query.tags.length === 0 || query.tags.some((t: string) => c.tags.includes(t))))
        .sort(COMPARATORS[query.sort]);
}

/** @returns count of causes per key (for "Education (4)" labels). */
export function countBy<K extends string>(causes: readonly Cause[], key: (c: Cause) => K): Map<K, number> {
    const counts: Map<K, number> = new Map();
    for (const cause of causes) {
        const k: K = key(cause);
        counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return counts;
}

/** @returns every distinct tag, by frequency then name (facets come from data). */
export function allTags(causes: readonly Cause[]): string[] {
    const counts: Map<string, number> = new Map();
    for (const cause of causes) for (const tag of cause.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
}

/** @returns a new array with `value` toggled in or out (for chip filters). */
export function toggle<T>(list: readonly T[], value: T): T[] {
    return list.includes(value) ? list.filter((v: T) => v !== value) : [...list, value];
}
